import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Facebook,
  RefreshCw,
  Loader2,
  Search,
  User,
  Mail,
  Phone,
  Building,
  UserPlus,
  CheckCircle,
  CheckCircle2,
  ExternalLink,
  Trash2,
  ArrowRight,
  Calendar,
  AlertCircle,
  Shield,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DuplicateBadge } from '../components/DuplicateBadge';
import { LeadSendEmailDialog } from '../components/LeadSendEmailDialog';
import { BulkEmailLeadsDialog } from '../components/BulkEmailLeadsDialog';
import { Checkbox } from '../components/ui/checkbox';

export const MetaLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fixingNames, setFixingNames] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [emailDialogLead, setEmailDialogLead] = useState(null);
  const [filter, setFilter] = useState('all');
  const [formNameFilter, setFormNameFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [convertDialog, setConvertDialog] = useState(false);
  const [converting, setConverting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [duplicates, setDuplicates] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    const filtered = leads.filter((lead) => {
      const q = searchQuery.toLowerCase();
      if (!q) return true;
      return (
        lead.first_name?.toLowerCase().includes(q) ||
        lead.last_name?.toLowerCase().includes(q) ||
        lead.email?.toLowerCase().includes(q) ||
        lead.phone?.includes(q)
      );
    });
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((l) => l.id)));
    }
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const params = { skip: page * 50, limit: 50 };
      if (filter === 'new') params.converted = false;
      if (filter === 'converted') params.converted = true;

      const response = await api.get('/meta-leads', { params });
      setLeads(response.data.leads || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Meta leads fetch error:', error.response?.status, error.response?.data);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      }
      toast.error(error.response?.data?.detail || 'Failed to load Meta leads');
      setLeads([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const highlightId = new URLSearchParams(window.location.search).get('highlight');
  useEffect(() => {
    if (!highlightId || !leads.length) return;
    const el = document.querySelector(`[data-testid="lead-row-${highlightId}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightId, leads]);

  useEffect(() => {
    api.get('/duplicates/scan').then(res => {
      setDuplicates(res.data?.meta_leads || {});
    }).catch(() => {});
  }, [leads]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/meta-leads/sync');
      toast.success(response.data.message);
      fetchLeads();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to sync leads';
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  const handleConvertLead = async () => {
    if (!selectedLead) return;
    setConverting(true);
    try {
      const response = await api.post(`/meta-leads/${selectedLead.id}/convert`);
      toast.success('Lead converted to customer!');
      setConvertDialog(false);
      setSelectedLead(null);
      fetchLeads();
      navigate(`/customers/${response.data.customer_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    } finally {
      setConverting(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.delete(`/meta-leads/${leadId}`);
      toast.success('Lead deleted');
      fetchLeads();
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const handleFixFormNames = async () => {
    setFixingNames(true);
    try {
      const response = await api.post('/meta-leads/fix-form-names');
      toast.success(response.data.message);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix form names');
    } finally {
      setFixingNames(false);
    }
  };

  const filteredLeads = leads.filter((lead) => {
    // Form name filter
    if (formNameFilter !== 'all') {
      const fn = lead.form_name || 'Lead Ads';
      if (fn !== formNameFilter) return false;
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      lead.first_name?.toLowerCase().includes(q) ||
      lead.last_name?.toLowerCase().includes(q) ||
      lead.email?.toLowerCase().includes(q) ||
      lead.phone?.includes(q) ||
      lead.company?.toLowerCase().includes(q) ||
      lead.street_address?.toLowerCase().includes(q) ||
      lead.post_code?.toLowerCase().includes(q) ||
      lead.raw_data?.street_address?.toLowerCase().includes(q) ||
      lead.raw_data?.post_code?.toLowerCase().includes(q)
    );
  });

  // Distinct form_name list for the filter dropdown (current page set)
  const distinctFormNames = Array.from(
    new Set(leads.map((l) => l.form_name || 'Lead Ads').filter(Boolean))
  ).sort();

  const newCount = leads.filter((l) => !l.converted).length;
  const convertedCount = leads.filter((l) => l.converted).length;

  if (accessDenied) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-24 text-slate-500" data-testid="meta-leads-access-denied">
          <Shield className="w-16 h-16 mb-4 text-slate-300" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Access Restricted</h2>
          <p>You don't have permission to view Meta Leads. Contact your admin for access.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="meta-leads-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <Facebook className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Meta Leads</h1>
              <p className="text-slate-500 text-sm">Leads from Facebook Lead Ads</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-blue-100 text-blue-700 px-3 py-1">
              <UserPlus className="w-3 h-3 mr-1" />
              {newCount} New
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1">
              <CheckCircle className="w-3 h-3 mr-1" />
              {convertedCount} Converted
            </Badge>
            <Button
              onClick={handleFixFormNames}
              disabled={fixingNames}
              variant="outline"
              size="sm"
              data-testid="fix-form-names-btn"
            >
              {fixingNames ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <AlertCircle className="w-4 h-4 mr-2" />
              )}
              Fix Form Names
            </Button>
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="sync-leads-btn"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync Leads
            </Button>
          </div>
        </div>

        <div className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="py-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name, email, phone..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-leads"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {['all', 'new', 'converted'].map((f) => (
                      <Button
                        key={f}
                        size="sm"
                        variant={filter === f ? 'default' : 'outline'}
                        onClick={() => { setFilter(f); setPage(0); }}
                        data-testid={`filter-${f}`}
                        className={filter === f ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      >
                        {f === 'all' ? 'All' : f === 'new' ? 'New' : 'Converted'}
                      </Button>
                    ))}
                    <div className="w-48">
                      <Select value={formNameFilter} onValueChange={setFormNameFilter}>
                        <SelectTrigger data-testid="form-name-filter" className="h-9">
                          <SelectValue placeholder="All forms" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All forms</SelectItem>
                          {distinctFormNames.map((fn) => (
                            <SelectItem key={fn} value={fn} data-testid={`form-option-${fn}`}>{fn}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
              <Card className="border-blue-300 bg-blue-50">
                <CardContent className="py-3 flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {selectedIds.size} lead{selectedIds.size > 1 ? 's' : ''} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => setBulkEmailOpen(true)}
                      data-testid="bulk-email-btn"
                    >
                      <Mail className="w-3 h-3 mr-1" /> Email Selected
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                      Clear
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Leads Table */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : filteredLeads.length === 0 ? (
                  <div className="text-center py-12">
                    <Facebook className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500 mb-2">No leads found</p>
                    <p className="text-sm text-slate-400 mb-4">
                      {leads.length === 0
                        ? "Click 'Sync Leads' to import leads from Facebook"
                        : 'No leads match your search criteria'}
                    </p>
                    {leads.length === 0 && (
                      <Button onClick={handleSync} disabled={syncing} variant="outline">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Sync Now
                      </Button>
                    )}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={
                              filteredLeads.length > 0 &&
                              selectedIds.size === filteredLeads.length
                            }
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Form / Ad</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLeads.map((lead) => (
                        <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`} className={lead.id === highlightId ? 'bg-sky-50 ring-2 ring-inset ring-sky-400' : ''}>
                          <TableCell>
                            <Checkbox
                              checked={selectedIds.has(lead.id)}
                              onCheckedChange={() => toggleSelect(lead.id)}
                              data-testid={`select-lead-${lead.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                                <User className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">
                                  {lead.first_name} {lead.last_name}
                                </p>
                                {duplicates[lead.id] && (
                                  <DuplicateBadge
                                    matches={duplicates[lead.id]}
                                    currentSource="meta_lead"
                                    currentId={lead.id}
                                    onMerged={fetchLeads}
                                    compact
                                  />
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-sm">
                                <Mail className="w-3 h-3 text-slate-400" />
                                <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">
                                  {lead.email}
                                </a>
                              </div>
                              {lead.phone && (
                                <div className="flex items-center gap-1 text-sm text-slate-500">
                                  <Phone className="w-3 h-3 text-slate-400" />
                                  {lead.phone}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {lead.company ? (
                              <div className="flex items-center gap-1 text-sm">
                                <Building className="w-3 h-3 text-slate-400" />
                                {lead.company}
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {(lead.street_address || lead.post_code || lead.raw_data?.street_address) ? (
                              <div className="flex items-start gap-1 text-sm">
                                <MapPin className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                                <div>
                                  {(lead.street_address || lead.raw_data?.street_address) && (
                                    <p className="text-slate-700">{lead.street_address || lead.raw_data?.street_address}</p>
                                  )}
                                  {(lead.post_code || lead.raw_data?.post_code) && (
                                    <p className="text-slate-500 text-xs">{lead.post_code || lead.raw_data?.post_code}</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <Badge variant="outline" className="text-xs font-medium">
                                <Facebook className="w-3 h-3 mr-1" />
                                {lead.form_name || 'Lead Ads'}
                              </Badge>
                              {lead.ad_name && (
                                <p className="text-[11px] text-slate-500 pl-0.5" data-testid={`lead-ad-${lead.id}`}>
                                  Ad: {lead.ad_name}
                                </p>
                              )}
                              {lead.campaign_name && (
                                <p className="text-[11px] text-slate-400 pl-0.5" data-testid={`lead-campaign-${lead.id}`}>
                                  Campaign: {lead.campaign_name}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm text-slate-500">
                              <Calendar className="w-3 h-3" />
                              {formatDate(lead.created_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            {lead.converted ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Converted
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700">New Lead</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-2">
                              {lead.email && (
                                <Button
                                  size="sm"
                                  variant={lead.email_log?.length > 0 ? "default" : "outline"}
                                  className={lead.email_log?.length > 0 ? "bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-700" : ""}
                                  onClick={() => setEmailDialogLead(lead)}
                                  data-testid={`email-btn-${lead.id}`}
                                  title={lead.email_log?.length > 0 ? `${lead.email_log.length} email(s) sent — click to send another` : 'Send email'}
                                >
                                  {lead.email_log?.length > 0 ? (
                                    <>
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Email Sent{lead.email_log.length > 1 ? ` (${lead.email_log.length})` : ''}
                                    </>
                                  ) : (
                                    <>
                                      <Mail className="w-3 h-3 mr-1" />
                                      Email
                                    </>
                                  )}
                                </Button>
                              )}
                              {!lead.converted ? (
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => {
                                    setSelectedLead(lead);
                                    setConvertDialog(true);
                                  }}
                                  data-testid={`convert-btn-${lead.id}`}
                                >
                                  <UserPlus className="w-3 h-3 mr-1" />
                                  Create Customer
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/customers/${lead.customer_id}`)}
                                  data-testid={`view-customer-btn-${lead.id}`}
                                >
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  View Customer
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteLead(lead.id)}
                                data-testid={`delete-btn-${lead.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {total > 50 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Showing {page * 50 + 1} - {Math.min((page + 1) * 50, total)} of {total} leads
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={(page + 1) * 50 >= total} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>

        {/* Convert Lead Dialog */}
        <Dialog open={convertDialog} onOpenChange={setConvertDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-600" />
                Convert Lead to Customer
              </DialogTitle>
              <DialogDescription>
                This will create a new customer record from the lead information.
              </DialogDescription>
            </DialogHeader>

            {selectedLead && (
              <div className="py-4 space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-lg">
                        {selectedLead.first_name} {selectedLead.last_name}
                      </p>
                      <p className="text-sm text-slate-500">Meta Lead</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-500">Email:</span>
                      <p className="font-medium">{selectedLead.email}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Phone:</span>
                      <p className="font-medium">{selectedLead.phone || '-'}</p>
                    </div>
                    {selectedLead.company && (
                      <div className="col-span-2">
                        <span className="text-slate-500">Company:</span>
                        <p className="font-medium">{selectedLead.company}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-amber-800 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p>
                    A new customer will be created with status "Lead". You can edit additional details after conversion.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setConvertDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConvertLead}
                disabled={converting}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-convert-btn"
              >
                {converting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Create Customer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <LeadSendEmailDialog
        open={!!emailDialogLead}
        onOpenChange={(v) => !v && setEmailDialogLead(null)}
        lead={emailDialogLead}
        leadSource="meta"
      />

      <BulkEmailLeadsDialog
        open={bulkEmailOpen}
        onOpenChange={setBulkEmailOpen}
        selectedLeads={leads
          .filter((l) => selectedIds.has(l.id))
          .map((l) => ({
            id: l.id,
            email: l.email,
            first_name: l.first_name,
            last_name: l.last_name,
            name: `${l.first_name || ''} ${l.last_name || ''}`.trim(),
            phone: l.phone,
          }))}
        leadSource="meta"
        onComplete={fetchLeads}
      />
    </Layout>
  );
};
